import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { MarkerInfo } from 'src/app/components/data-set/data-set-map.component';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	RemoveDeviceDialogComponent,
	RemoveDeviceDialogData,
} from 'src/app/components/remove-device-dialog/remove-device-dialog.component';
import { Company } from 'src/app/models/data/company';
import {
	Device,
	DEVICE_MARKETING_NAME_SENTRIVAULT,
	DeviceAccessSource,
	DeviceDataRequest,
	PDQ_KMS_GENERATION_1,
	PDQ_SDS_GENERATION_1,
} from 'src/app/models/data/device';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	DeviceRemovalActions,
} from 'src/app/services/api.services';
import { BannerMessageService } from 'src/app/services/banner-message.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { DeviceDetailsService } from 'src/app/services/device-details.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPresentationToggleComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableMenuComponent,
} from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { OverflowChipListComponent } from '../../../../components/overflow-chip-list/overflow-chip-list.component';
import { DeviceStatusComponent } from '../../device-status/device-status.component';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { DataSetMapViewComponent } from '../../../../components/data-set/data-set-map.component';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';
import {
	allowWrites,
	isEmbedded,
	ownershipCompanyId as resolveOwnershipCompanyId,
} from '../company-detail-embed';

@Component({
	selector: 'app-all-devices',
	templateUrl: './all-devices.component.html',
	styleUrls: [
		'./all-devices.component.scss',
		'../../../common/main.scss',
		'../../../../../values.scss',
	],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetPresentationToggleComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		OverflowChipListComponent,
		DeviceStatusComponent,
		MatIcon,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DataSetMapViewComponent,
		DataSetLoadingRowComponent,
	],
})
export class AllDevicesComponent implements AfterViewInit, OnDestroy, OnInit {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
	@Input() deviceDataRequest: DeviceDataRequest = (filter) =>
		this.apiService.getAllDevices(filter);
	@Input() hideToolbar = false; // Hide search/filter toolbar (used for single property's devices)
	/** Route company when embedded on company detail (ITE-12004). */
	@Input() companyId?: string;
	@Input() company?: Company;
	/** When embedded, parent controls write chrome (Oracle vs Support/Billing). */
	@Input() canWrite?: boolean;

	// Do NOT set this variable to true! Only used for unit tests.
	@Input() isUnitTesting = false;

	canEditDevices = false;
	activeCompanyId: string | undefined = '';
	companyHasDeviceRemovalAccessOptionsEnabled: boolean = false;

	get isEmbeddedInCompanyDetail(): boolean {
		return isEmbedded(this.companyId);
	}

	/** Write chrome: DM on left-nav; parent canWrite when embedded. */
	get allowDeviceWrites(): boolean {
		return allowWrites(this.companyId, this.canWrite, this.canEditDevices);
	}

	/** Company used for owned/inherited Source and removal ownership checks. */
	get ownershipCompanyId(): string | undefined {
		return resolveOwnershipCompanyId(this.companyId, this.activeCompanyId);
	}
	displayedColumns: string[] = [
		'serialNumber',
		'propertyName',
		'deviceLocation',
		'group',
		'displayStatus',
		'source',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<{
		device: Device;
		groupNames?: string[];
	}>();
	localizePropertyName = $localize`:@@allDeviceTable-propertyNameLabel:Property Name`;

	filterLabels = {
		defaultPlaceholder: $localize`:@@allDevices-defaultPlaceholder:Search serial number...`,
		serialNumber: $localize`:@@allDevices-filterLabelSerialNumber:Serial Number`,
		deviceGroups: $localize`:@@allDevices-filterLabelDeviceGroups:Device Group(s)`,
		status: $localize`:@@allDevices-filterLabelStatus:Status`,
		propertyName: $localize`:@@allDevices-filterLabelPropertyName:Property Name`,
		streetAddress: $localize`:@@allDevices-filterLabelStreetAddress:Street Address`,
		townCity: $localize`:@@allDevices-filterLabelTownCity:Town / City`,
		stateProvince: $localize`:@@allDevices-filterLabelStateProvince:State / Province`,
		postalCode: $localize`:@@allDevices-filterLabelPostalCode:Postal Code`,
		country: $localize`:@@allDevices-filterLabelCountry:Country`,
	};

	private filterSubscription?: Subscription;
	private systemCapabilitiesSubscription?: Subscription;

	markersInfo: MarkerInfo[] = [];

	getAllDevicesLoadingKey = ApiLoadingKeys.getAllDevices;
	finishedLoadingInitData: boolean = false;

	PDQ_KMS_GENERATION_1 = PDQ_KMS_GENERATION_1;
	PDQ_SDS_GENERATION_1 = PDQ_SDS_GENERATION_1;
	isDeviceMarketingNameSentriVault = DEVICE_MARKETING_NAME_SENTRIVAULT;

	constructor(
		private readonly apiService: ApiService,
		private readonly userSessionService: UserSessionService,
		private readonly scService: SystemCapabilityService,
		private bannerMessageService: BannerMessageService,
		private readonly router: Router,
		private readonly deviceDetailsService: DeviceDetailsService,
		public readonly loadingService: LoadingIndicatorService,
		private dialog: MatDialog
	) {
		this.refreshCanEditDevices();
	}

	async ngOnInit() {
		this.systemCapabilitiesSubscription =
			this.scService.systemCapabilitiesUpdated.subscribe(() => {
				this.refreshCanEditDevices();
			});
		this.refreshCanEditDevices();

		await this.setActiveCompanyId();
		await this.loadDeviceRemovalAccessOptions();
	}

	private async loadDeviceRemovalAccessOptions(): Promise<void> {
		// Embedded: use route company payload (not the caller's active-company setting).
		if (this.isEmbeddedInCompanyDetail) {
			this.companyHasDeviceRemovalAccessOptionsEnabled =
				!!this.company?.deviceRemovalAccessOptions;
			return;
		}

		try {
			const companySetting = await this.apiService.getCompanySettingByName(
				'deviceRemovalAccessOptions'
			);
			this.companyHasDeviceRemovalAccessOptionsEnabled =
				companySetting?.value?.toLowerCase() === 'true' ||
				companySetting?.value === '1';
		} catch (err) {
			// If setting doesn't exist or error occurs, default to false (no dialog)
			this.companyHasDeviceRemovalAccessOptionsEnabled = false;
		}
	}

	ngAfterViewInit(): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.deviceDataRequest(filter);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.dataSource.data = data;
				this.setMapMarkers();
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
		this.systemCapabilitiesSubscription?.unsubscribe();
		this.systemCapabilitiesSubscription = undefined;
	}

	private refreshCanEditDevices(): void {
		this.canEditDevices = this.scService.has(
			SystemCapabilityEnum.deviceManagement
		);
	}

	async setActiveCompanyId(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;
	}

	onRowClick(element: {
		device: Device;
		groupNames: string[];
		groupIds: string[];
	}): void {
		this.deviceDetailsService.saveDeviceDetails(element.device);
		if (this.companyId) {
			this.router.navigate([
				`/company/${this.companyId}/detail/device/${element.device.id}/detail`,
			]);
			return;
		}
		this.router.navigate(['/devices', element.device.id]);
	}

	isOwnedDeviceGroup(source: DeviceAccessSource): boolean {
		const companyId = resolveOwnershipCompanyId(
			this.companyId,
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId
		);
		return source?.companyId === companyId;
	}

	canShowDeviceRowMenu(row: { device: Device }): boolean {
		const ownerCompanyId = row.device.activeDeviceAssignment?.companyId;
		return (
			this.allowDeviceWrites &&
			!!this.ownershipCompanyId &&
			this.ownershipCompanyId === ownerCompanyId &&
			row.device.status === 'inService' &&
			row.device.deviceGeneration !== PDQ_KMS_GENERATION_1 &&
			row.device.deviceGeneration !== PDQ_SDS_GENERATION_1 &&
			row.device.deviceType !== this.isDeviceMarketingNameSentriVault
		);
	}

	onRowMenuReadyRemovalClicked(element: {
		device: Device;
		groupNames: string[];
		groupIds: string[];
	}): void {
		// Check if company has device removal dialog setting enabled
		if (this.companyHasDeviceRemovalAccessOptionsEnabled) {
			this.openRemoveDeviceDialog(element.device);
		} else {
			// Direct removal request
			this.markDeviceReadyForRemoval(element.device);
		}
	}

	openRemoveDeviceDialog(device: Device): void {
		const dialogRef = this.dialog.open(RemoveDeviceDialogComponent, {
			width: '510px',
			panelClass: 'mobile-friendly-curved-action-dialog',
			data: {
				deviceId: device.id,
				deviceName: device.serialNumber,
			} as RemoveDeviceDialogData,
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result && result.success) {
				// Update the device state to reflect removal request
				if (device.activeDeviceAssignment?.activeInstallation) {
					device.activeDeviceAssignment.activeInstallation.utcRemovalRequested =
						new Date().toISOString();
				}
			}
		});
	}

	async markDeviceReadyForRemoval(device: Device): Promise<void> {
		try {
			const removalRequestResponse =
				await this.apiService.requestDeviceRemovalAction(
					device.id,
					DeviceRemovalActions.request
				);
			this.bannerMessageService.sendMessage(
				$localize`:@@allDevices-deviceRemovalRequested:Device removal requested.`
			);
			if (device.activeDeviceAssignment?.activeInstallation) {
				device.activeDeviceAssignment.activeInstallation.utcRemovalRequested =
					new Date().toISOString();
			}
		} catch (err) {
			this.bannerMessageService.sendMessage(
				$localize`:@@allDevices-errorRequestingDeviceRemoval:An error occurred while requesting device removal. Please try again.`
			);
		}
	}

	onRowMenuClearRemovalClicked(element: {
		device: Device;
		groupNames: string[];
		groupIds: string[];
	}): void {
		this.clearReadyForRemoval(element.device);
	}

	async clearReadyForRemoval(device: Device): Promise<void> {
		try {
			const removalRequestResponse =
				await this.apiService.requestDeviceRemovalAction(
					device.id,
					DeviceRemovalActions.cancel
				);
			this.bannerMessageService.sendMessage(
				$localize`:@@allDevices-canceledDeviceRemovalRequest:Canceled device removal request.`
			);
			if (device.activeDeviceAssignment?.activeInstallation) {
				device.activeDeviceAssignment.activeInstallation.utcRemovalRequested =
					undefined;
			}
		} catch (err) {
			this.bannerMessageService.sendMessage(
				$localize`:@@allDevices-errorClearingRemovalStatus:An error occurred while clearing the removal status. Please try again.`
			);
		}
	}

	setMapMarkers(): MarkerInfo[] {
		this.markersInfo = [];
		this.dataSource.data.forEach(({ device }: any) => {
			if (
				device.activeDeviceAssignment?.activeInstallation?.installationLocation
					?.location?.address.geoCoordinates
			) {
				const position = {
					lat: device.activeDeviceAssignment?.activeInstallation
						?.installationLocation?.location?.address.geoCoordinates?.latitude,
					lng: device.activeDeviceAssignment?.activeInstallation
						?.installationLocation?.location?.address.geoCoordinates?.longitude,
				};

				const locationName =
					device.activeDeviceAssignment?.activeInstallation
						?.installationLocation?.location?.name ?? '';
				const address1 =
					device.activeDeviceAssignment?.activeInstallation
						?.installationLocation?.location?.address?.address1 ?? '';
				const locality =
					device.activeDeviceAssignment?.activeInstallation
						?.installationLocation?.location?.address?.locality ?? '';
				const postalCode =
					device.activeDeviceAssignment?.activeInstallation
						?.installationLocation?.location?.address?.postalCode ?? '';
				const deviceSn = device.serialNumber ?? '';
				const locationId =
					device.activeDeviceAssignment?.activeInstallation
						?.installationLocation?.location?.id ?? '';
				const deviceId = device.id ?? '';

				const title =
					locationName +
					'\n' +
					address1 +
					'\n' +
					locality +
					' ' +
					postalCode +
					'\n' +
					'SN: ' +
					deviceSn;

				const marker = {
					position,
					icon: '',
					title,
					clickable: true,
				};

				this.markersInfo.push({
					marker,
					deviceId,
					deviceSn,
					locationName,
					address1,
					locality,
					postalCode,
					locationId,
				});
			}
		});

		return this.markersInfo;
	}

	rowId(row: { device: Device; groupNames?: string[] }): string {
		return row?.device?.id || 'unknown-id';
	}
}
